import { COLORS } from 'contexts/UIProvider/constants';
import { TAG_TYPES } from 'types/services';
import { RECORD_VERSION_CREATION_MODE } from 'types/enums';
import type {
  AppSettingResponse,
  DashboardItem,
  GetColorsResponse,
  GetEmailForFeedbackResponse,
  TransformedSettingsResponse,
  UpdateAppSettingRequest,
  OldDashboardItem,
  GetContactsSettingsResponse,
  UpdateUserUISettingsRequest,
  UpdateUserUISettingsByUserIdRequest,
} from 'types';

import { coreApi } from '.';

export const settingsService = coreApi.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    getSettings: builder.query<TransformedSettingsResponse, void>({
      query: () => ({
        url: `AppSetting/Admin?globalUiSettingKeys=dashboard&${Object.keys(COLORS)
          .map((key) => `globalUiSettingKeys=${key}`)
          .join('&')}`,
        method: 'GET',
      }),
      transformResponse: (response: AppSettingResponse): TransformedSettingsResponse => {
        const color: Record<keyof typeof COLORS, string> = response.globalUiSettings.reduce(
          (acc, curr) => {
            if (curr.key !== 'dashboard' && COLORS[curr.key as keyof typeof COLORS]) {
              acc[curr.key as keyof typeof COLORS] = curr.value;
            }
            return acc;
          },
          {} as Record<keyof typeof COLORS, string>
        );

        const dashboardValue = response.globalUiSettings.find((setting) => setting.key === 'dashboard')?.value;

        let dashboard: DashboardItem[] | OldDashboardItem[];
        if (Array.isArray(dashboardValue)) {
          dashboard = dashboardValue;
        } else {
          dashboard = dashboardValue ? JSON.parse(dashboardValue) : [];
        }

        return {
          ...response,
          dashboard,
          color,
          recordVersionCreationMode: response.recordVersionCreationMode ?? RECORD_VERSION_CREATION_MODE.UI_UPDATES_ONLY,
        };
      },
      providesTags: [TAG_TYPES.SETTINGS],
    }),
    getUISettings: builder.query<GetColorsResponse, void>({
      query: () => ({
        url: 'UiSettings',
        method: 'GET',
      }),
      providesTags: [TAG_TYPES.SETTINGS],
    }),
    getUISettingsByUserId: builder.query<GetColorsResponse, string>({
      query: (userId) => ({
        url: `UiSettings/Users/${userId}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, userId) => [{ type: TAG_TYPES.SETTINGS, id: userId }],
    }),
    updateSettings: builder.mutation<AppSettingResponse, UpdateAppSettingRequest>({
      query: (data) => ({
        url: 'AppSetting/Admin',
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: [TAG_TYPES.SETTINGS],
    }),
    getEmailForBugReport: builder.query<GetEmailForFeedbackResponse, void>({
      query: () => ({
        url: 'AppSettings/EmailAddressBugReporting',
        method: 'GET',
      }),
      providesTags: [TAG_TYPES.SETTINGS],
    }),
    getContactsSettings: builder.query<GetContactsSettingsResponse, void>({
      query: () => ({
        url: 'AppSettings/ContactsSettings',
        method: 'GET',
      }),
      providesTags: [TAG_TYPES.SETTINGS],
    }),
    updateUserUISettings: builder.mutation<void, UpdateUserUISettingsRequest>({
      query: (data) => ({
        url: 'UiSettings',
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: [TAG_TYPES.SETTINGS],
    }),
    updateUserUISettingsByUserId: builder.mutation<void, UpdateUserUISettingsByUserIdRequest>({
      query: ({ userId, uiSettings }) => ({
        url: `UiSettings/Users/${userId}`,
        method: 'PUT',
        body: { uiSettings, userId },
      }),
      invalidatesTags: (_result, _error, arg) => [{ type: TAG_TYPES.SETTINGS, id: arg.userId }],
    }),
  }),
});
export const {
  useGetSettingsQuery,
  useLazyGetSettingsQuery,
  useUpdateSettingsMutation,
  useGetUISettingsQuery,
  useGetUISettingsByUserIdQuery,
  useGetEmailForBugReportQuery,
  useLazyGetUISettingsQuery,
  useLazyGetUISettingsByUserIdQuery,
  useLazyGetEmailForBugReportQuery,
  useGetContactsSettingsQuery,
  useLazyGetContactsSettingsQuery,
  useUpdateUserUISettingsMutation,
  useUpdateUserUISettingsByUserIdMutation,
} = settingsService;
